import React, { useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components/macro';

import { Label2, Small2, Small3, Small4, Value3 } from '../Text';

import { Tooltip } from '../Tooltip';

const Container = styled.div`
  width: 100%;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled.div<{ disabled: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${({ disabled, theme }) => (disabled ? theme.bg5 : theme.white)};
`;

const TooltipWrapper = styled.div`
  margin-left: 0.5rem;
`;

const DirectionLabel = styled(Small3)<{ isLong?: boolean; disabled: boolean }>`
  margin-left: 0.5rem;
  background: ${({ theme, disabled }) => (disabled ? theme.bg3 : theme.gray0)};
  color: ${({ theme, isLong, disabled }) => {
    if (disabled) {
      return theme.bg5;
    }
    if (!isLong) {
      return theme.red;
    }
    return theme.green;
  }};
  border-radius: 0.375rem;
  padding: 0.25rem 0.375rem;
`;

const DisplaySelected = styled(Value3)<{ disabled: boolean }>`
  margin-left: auto;
  background: ${({ theme, disabled }) => (disabled ? theme.bg3 : theme.gray0)};
  color: ${({ theme, disabled }) => (disabled ? theme.bg5 : theme.white)};
  border-radius: 0.5rem;
  padding: 0.25rem 0.5625rem;
`;

const Slider = styled.input<{ disabled: boolean }>`
  -webkit-appearance: none;
  height: 0.375rem;
  background: ${({ theme, disabled }) => (disabled ? theme.bg4 : theme.lgrad1)};
  border: 0.25rem solid ${({ theme, disabled }) => (disabled ? theme.bg3 : theme.bg0)};
  border-radius: 1.875rem;
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 1.875rem;
    height: 1.875rem;
    border-radius: 0.95rem;
    background: ${({ theme }) => theme.bg4};
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 1.875rem;
    height: 1.875rem;
    border-radius: 0.95rem;
    background: ${({ theme }) => theme.bg4};
    border-color: #00000000;
    cursor: pointer;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0 0.25rem;
  padding: 0 1rem;
`;

const LegendValue = styled(Small4)<{ disabled: boolean }>`
  color: ${({ theme, disabled }) => (disabled ? theme.gray3 : theme.gray4)};
  text-align: center;
  margin-bottom: 0.25rem;
`;

const LeverageWarning = styled.div<{ visible: boolean; selectorPosition: number }>`
  position: relative;
  overflow: hidden;
  margin: 0;
  inline-size: auto;
  color: ${({ theme }) => theme.white};
  border-radius: 0.75rem;
  transition: all ${({ theme }) => theme.fast} ease-out;
  max-height: ${({ visible }) => (visible ? '100vh' : '0vh')};

  &:before {
    content: '';
    display: block;
    position: absolute;
    width: 0;
    height: 0;
    top: 0;
    right: ${({ selectorPosition }) => 1 + (1 - selectorPosition) * 90}%;
    transform: translateX(-80%);
    border-left: 0.375rem solid transparent;
    border-right: 0.375rem solid transparent;
    border-bottom: 0.375rem solid ${({ theme }) => theme.redAlpha};
  }
`;

const WarningText = styled(Small2)`
  inline-size: auto;
  background: ${({ theme }) => theme.redAlpha};
  color: ${({ theme }) => theme.white};
  border-radius: 0.75rem;
  padding: 0.75rem;
  margin: 0;
  margin-top: 0.375rem;

  a {
    display: block;
    margin-top: 0.25rem;
    color: ${({ theme }) => theme.white};
    text-decoration: none;
    font-weight: 600;
  }
`;

interface LeverageSliderProps {
  isLongLabel: boolean;
  max: number;
  showMarginWarning: boolean;
  value?: number;
  onChange: (selected: number) => void;
  disabled?: boolean;
}

export function LeverageSlider({
  isLongLabel,
  max,
  showMarginWarning,
  value,
  onChange,
  disabled = false,
}: LeverageSliderProps) {
  const { t } = useTranslation();

  const labels = ['0', '25', '50', '75', '100'];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rounded = (Number(e.target.value) * max) / 1000;
    if (rounded !== undefined) {
      onChange(rounded);
    }
  };

  const forcedValue = useMemo(() => (value ? (value / max) * 1000 : 0), [max, value]);

  const displayValue = useMemo<string>(
    () => Math.abs(value ? (value / max) * 100 : 0).toFixed(2),
    [value, max],
  );

  return (
    <Container>
      <HeaderContainer disabled={disabled}>
        <Label2>{t('Position size')}</Label2>
        <TooltipWrapper>
          <Tooltip size="m" position="top" offset={0.33} disabled={disabled}>
            {t(
              'Higher leverage magnifies both gains and losses (liquidation). Ex. You can open a $500 position with $100 at 5x leverage.',
            )}
          </Tooltip>
        </TooltipWrapper>
        <DirectionLabel disabled={disabled} isLong={isLongLabel}>
          {isLongLabel ? t('LONG') : t('SHORT')}
        </DirectionLabel>
        <DisplaySelected disabled={disabled}>{displayValue}%</DisplaySelected>
      </HeaderContainer>
      <Slider
        type="range"
        min={0}
        max={1000}
        onChange={handleChange}
        value={forcedValue}
        disabled={disabled}
      />
      <LegendContainer>
        {labels?.map((item) => (
          <LegendValue key={item} disabled={disabled}>
            {item}%
          </LegendValue>
        ))}
      </LegendContainer>
      <LeverageWarning visible={showMarginWarning} selectorPosition={(value ?? 0) / max}>
        <WarningText>
          <Trans i18nKey="leverageWarningText">
            <b>Caution, likely insufficient margin. </b>Cannot open a position that moves your
            account leverage past 20x - this is likely due to price impact and instant PnL. Try
            reducing your position size if you are unable to open your position.
          </Trans>
        </WarningText>
      </LeverageWarning>
    </Container>
  );
}

LeverageSlider.defaultProps = {
  disabled: false,
  value: undefined,
};
